import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@matic/lib/utils/cn';

const spinnerVariants = cva('animate-spin', {
  variants: {
    size: {
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type SpinnerProps = {
  /**
   * Accessible label for screen readers
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention -- HTML attribute name
  'aria-label'?: string;
} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof spinnerVariants>;

/**
 * Spinner component - Loading indicator
 *
 * Uses currentColor by default, so it inherits text color from parent.
 * Set text color via className to change spinner color.
 *
 * @example
 * // Default (inherits color)
 * <Spinner size="md" />
 *
 * // With custom color
 * <div className="text-primary-600">
 *   <Spinner size="lg" />
 * </div>
 *
 * // Inline with custom color
 * <Spinner size="sm" className="text-primary-600" />
 */
export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({className, size, 'aria-label': ariaLabel = 'Loading', ...props}, ref) => {
    return (
      <div
        ref={ref}
        className={cn(spinnerVariants({size, className}))}
        role="status"
        aria-label={ariaLabel}
        {...props}
      >
        <svg
          className="h-full w-full"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="sr-only">{ariaLabel}</span>
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';

export default Spinner;
